import React, { useEffect, useState } from 'react';
import AuthorizationService from '../../Authorization/authorizationService';
import DiagnosisCombinerService from '../../Authorization/diagnosisCombinerService';

const AuthorizationSection = ({ authorizations = [], userRole }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedAuth, setEditedAuth] = useState({});
  const [editedDiag, setEditedDiag] = useState({});
  const [diagnosisMap, setDiagnosisMap] = useState({});

  const normalizedRole = userRole?.replace('ROLE_', '') || '';

  const canEdit =
    normalizedRole === 'ADMIN' ||
    normalizedRole === 'MANAGER' ||
    normalizedRole === 'FRONTDESK';

  useEffect(() => {
    const fetchAllDiagnosisData = async () => {
      const diagService = new DiagnosisCombinerService();
      const map = {};

      for (const auth of authorizations) {
        try {
          const res = await diagService.getByAuthId(auth.authId);

          if (Array.isArray(res)) {
            map[auth.authId] =
              res.length > 0
                ? res[0]
                : {
                    diagCombId: auth.diagCombId || null,
                    authId: auth.authId,
                    diagnosisSet: auth.diagnosisSet || '',
                    procedureSet: auth.procedureSet || '',
                  };
          } else if (res) {
            map[auth.authId] = {
              diagCombId: res.diagCombId || auth.diagCombId || null,
              authId: res.authId || auth.authId,
              diagnosisSet: res.diagnosisSet || auth.diagnosisSet || '',
              procedureSet: res.procedureSet || auth.procedureSet || '',
            };
          } else {
            map[auth.authId] = {
              diagCombId: auth.diagCombId || null,
              authId: auth.authId,
              diagnosisSet: auth.diagnosisSet || '',
              procedureSet: auth.procedureSet || '',
            };
          }
        } catch {
          map[auth.authId] = {
            diagCombId: auth.diagCombId || null,
            authId: auth.authId,
            diagnosisSet: auth.diagnosisSet || '',
            procedureSet: auth.procedureSet || '',
          };
        }
      }

      setDiagnosisMap(map);
    };

    if (authorizations.length > 0) {
      fetchAllDiagnosisData();
    } else {
      setDiagnosisMap({});
    }
  }, [authorizations]);

  useEffect(() => {
    if (authorizations.length === 0) {
      setCurrentIndex(0);
      setEditingIndex(null);
      return;
    }

    if (currentIndex >= authorizations.length) {
      setCurrentIndex(0);
      setEditingIndex(null);
    }
  }, [authorizations, currentIndex]);

  useEffect(() => {
    const handleInsuranceRecordMessage = (event) => {
      const { inputTarget, value, displayName } = event.data || {};

      if (inputTarget !== 'insptId') {
        return;
      }

      if (!value) {
        return;
      }

      setEditedAuth((prev) => ({
        ...prev,
        inspt_id: value,
        insptId: value,
        insurancePatientDisplayName: displayName || prev.insurancePatientDisplayName || '',
      }));
    };

    window.addEventListener('message', handleInsuranceRecordMessage);

    return () => {
      window.removeEventListener('message', handleInsuranceRecordMessage);
    };
  }, []);

  const getAuthorizationInsurancePatientId = (auth) => {
    return (
      auth?.inspt_id ||
      auth?.insptId ||
      auth?.insurancePatientId ||
      auth?.insurance_patient_id ||
      ''
    );
  };

  const getAuthorizationPatientId = (auth) => {
    return auth?.patientId || auth?.patient_id || auth?.patientID || '';
  };

  const openInsuranceRecordSelector = () => {
    const patientId = getAuthorizationPatientId(editedAuth);

    if (!patientId) {
      alert('Patient ID missing for this authorization. Cannot select insurance patient record.');
      return;
    }

    const url = `/insurance-record-selector?patientId=${encodeURIComponent(
      patientId
    )}`;

    window.open(url, 'popup', 'width=700,height=600');
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setEditingIndex(null);
    setEditedAuth({});
    setEditedDiag({});
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, authorizations.length - 1));
    setEditingIndex(null);
    setEditedAuth({});
    setEditedDiag({});
  };

  const handleEdit = (index) => {
    const auth = authorizations[index];

    const diag = diagnosisMap[auth.authId] || {
      diagCombId: auth.diagCombId || null,
      authId: auth.authId,
      diagnosisSet: auth.diagnosisSet || '',
      procedureSet: auth.procedureSet || '',
    };

    const currentInsptId = getAuthorizationInsurancePatientId(auth);

    setEditingIndex(index);

    setEditedAuth({
      ...auth,
      inspt_id: currentInsptId,
      insptId: currentInsptId,
      patientId: getAuthorizationPatientId(auth),
      totalVisits:
        auth.totalVisits !== undefined && auth.totalVisits !== null
          ? auth.totalVisits
          : '',
      visits:
        auth.visits !== undefined && auth.visits !== null ? auth.visits : '',
    });

    setEditedDiag({
      diagCombId: diag.diagCombId || auth.diagCombId || null,
      authId: auth.authId,
      diagnosisSet: diag.diagnosisSet || auth.diagnosisSet || '',
      procedureSet: diag.procedureSet || auth.procedureSet || '',
    });
  };

  const handleChangeAuth = (e) => {
    const { name, value } = e.target;

    setEditedAuth((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangeDiag = (e) => {
    const { name, value } = e.target;

    setEditedDiag((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const cleanCommaSeparatedString = (value) => {
    if (!value) {
      return '';
    }

    return String(value)
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '')
      .join(', ');
  };

  const isSimpleNoAuthorizationCode = (authCode) => {
    if (!authCode) return false;

    const cleaned = String(authCode).trim();

    if (!/^\d+$/.test(cleaned)) {
      return false;
    }

    const number = parseInt(cleaned, 10);

    return number >= 1 && number <= 99;
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';

    const parts = String(value).split('-');

    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;

    return `${month}-${day}-${year}`;
  };

  const parseNumberOrZero = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const handleSave = async (authId) => {
    try {
      const authService = new AuthorizationService();
      const diagService = new DiagnosisCombinerService();

      const selectedInsptId =
        editedAuth.inspt_id ||
        editedAuth.insptId ||
        editedAuth.insurancePatientId ||
        '';

      if (!selectedInsptId) {
        alert('Please select an insurance patient record before saving.');
        return;
      }

      const payload = {
        ...editedAuth,
        patientId: parseNumberOrZero(editedAuth.patientId),
        inspt_id: parseNumberOrZero(selectedInsptId),
        visits: parseNumberOrZero(editedAuth.visits),
        totalVisits: parseNumberOrZero(editedAuth.totalVisits),
        weeklyVisitLimit: parseNumberOrZero(editedAuth.weeklyVisitLimit),
      };

      await authService.editAuthorization(authId, payload);

      const diagPayload = {
        ...editedDiag,
        authId,
        diagnosisSet: cleanCommaSeparatedString(editedDiag.diagnosisSet),
        procedureSet: cleanCommaSeparatedString(editedDiag.procedureSet),
      };

      const savedDiag = await diagService.saveOrUpdate(diagPayload);

      const finalDiag = savedDiag || diagPayload;

      setDiagnosisMap((prev) => ({
        ...prev,
        [authId]: {
          ...finalDiag,
          diagnosisSet: finalDiag.diagnosisSet || diagPayload.diagnosisSet,
          procedureSet: finalDiag.procedureSet || diagPayload.procedureSet,
        },
      }));

      setEditingIndex(null);
      setEditedAuth({});
      setEditedDiag({});

      alert('Authorization updated successfully.');
      window.location.reload();
    } catch (e) {
      alert('Failed to save changes: ' + (e.message || 'Unknown error'));
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedAuth({});
    setEditedDiag({});
  };

  const handleDelete = async (authId) => {
    if (!window.confirm('Are you sure you want to delete this authorization?')) {
      return;
    }

    try {
      const service = new AuthorizationService();

      await service.removeAuthorization(authId);

      alert('Authorization deleted successfully.');
      window.location.reload();
    } catch (e) {
      alert('Failed to delete: ' + (e.message || 'Unknown error'));
    }
  };

  const formatCommaSeparatedList = (str) => {
    if (!str || String(str).trim() === '') {
      return <i>None</i>;
    }

    return String(str)
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item !== '')
      .map((item, index) => <li key={`${item}-${index}`}>{item}</li>);
  };

  if (!authorizations || authorizations.length === 0) {
    return (
      <div className="authorization-section paged-section">
        <h2>Authorizations</h2>
        <p>No authorizations found.</p>
      </div>
    );
  }

  const auth = authorizations[currentIndex];

  const diag = diagnosisMap[auth.authId] || {
    diagCombId: auth.diagCombId || null,
    authId: auth.authId,
    diagnosisSet: auth.diagnosisSet || '',
    procedureSet: auth.procedureSet || '',
  };

  const isEditing = editingIndex === currentIndex;
  const hideAuthInfo = isSimpleNoAuthorizationCode(auth.authCode);
  const displayedInsptId = getAuthorizationInsurancePatientId(auth);
  const editedInsptId =
    editedAuth.inspt_id ||
    editedAuth.insptId ||
    editedAuth.insurancePatientId ||
    '';

  return (
    <div className="authorization-section paged-section">
      <h2>Authorizations</h2>

      <div className="authorization-card paged-card">
        {isEditing ? (
          <>
            <div className="authorization-insurance-edit-box">
              <label>
                Insurance Patient Record:
                <input
                  name="inspt_id"
                  value={editedInsptId}
                  readOnly
                  className="edit-input"
                  placeholder="Selected insurance patient record"
                />
              </label>

              <small className="field-help-text">
                This controls which insurance-patient record this authorization uses.
              </small>

              <button
                type="button"
                className="edit-button"
                onClick={openInsuranceRecordSelector}
              >
                Change Insurance Patient
              </button>
            </div>

            <label>
              Auth Code:
              <input
                name="authCode"
                value={editedAuth.authCode || ''}
                onChange={handleChangeAuth}
                className="edit-input"
              />
            </label>

            <label>
              Start Date:
              <input
                type="date"
                name="dayStart"
                value={editedAuth.dayStart || ''}
                onChange={handleChangeAuth}
                className="edit-input"
              />
            </label>

            <label>
              End Date:
              <input
                type="date"
                name="dayEnd"
                value={editedAuth.dayEnd || ''}
                onChange={handleChangeAuth}
                className="edit-input"
              />
            </label>

            <label>
              Total Visits:
              <input
                type="number"
                name="totalVisits"
                value={editedAuth.totalVisits || ''}
                onChange={handleChangeAuth}
                className="edit-input"
              />
            </label>

            <label>
              Remaining Visits:
              <input
                type="number"
                name="visits"
                value={editedAuth.visits || ''}
                onChange={handleChangeAuth}
                className="edit-input"
              />
            </label>

            <label>
              Active:
              <select
                name="active"
                value={editedAuth.active || ''}
                onChange={handleChangeAuth}
                className="edit-input"
              >
                <option value="">Select Active Status</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label>
              Diagnosis Codes:
              <textarea
                name="diagnosisSet"
                value={editedDiag.diagnosisSet || ''}
                onChange={handleChangeDiag}
                className="edit-input"
                rows="4"
                placeholder="Example: M54.50, M25.511, M25.512"
              />
            </label>

            <small className="field-help-text">
              Enter multiple diagnosis codes separated by commas.
            </small>

            <label>
              Procedure Codes:
              <textarea
                name="procedureSet"
                value={editedDiag.procedureSet || ''}
                onChange={handleChangeDiag}
                className="edit-input"
                rows="4"
                placeholder="Example: 97110, 97140, 97530"
              />
            </label>

            <small className="field-help-text">
              Enter multiple procedure codes separated by commas.
            </small>

            <div className="paged-action-row">
              <button
                type="button"
                className="save-button"
                onClick={() => handleSave(auth.authId)}
              >
                Save
              </button>

              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            {hideAuthInfo ? (
              <>
                <p>
                  <strong>Authorization:</strong> N/A
                </p>

                <p>
                  <strong>Note:</strong> No formal authorization required.
                </p>
              </>
            ) : (
              <>
                <p>
                  <strong>Insurance Patient Record:</strong>{' '}
                  {displayedInsptId || 'N/A'}
                </p>

                <p>
                  <strong>Authorization Code:</strong>{' '}
                  {auth.authCode || 'N/A'}
                </p>

                <p>
                  <strong>Start Date:</strong> {formatDate(auth.dayStart)}
                </p>

                <p>
                  <strong>End Date:</strong> {formatDate(auth.dayEnd)}
                </p>

                <p>
                  <strong>Total Visits:</strong>{' '}
                  {auth.totalVisits ?? 'N/A'}
                </p>

                <p>
                  <strong>Remaining Visits:</strong>{' '}
                  {auth.visits ?? 'N/A'}
                </p>

                <p>
                  <strong>Active:</strong> {auth.active || 'N/A'}
                </p>
              </>
            )}

            <div>
              <strong>Diagnosis:</strong>
              <ul>{formatCommaSeparatedList(diag.diagnosisSet)}</ul>
            </div>

            <div>
              <strong>Procedure:</strong>
              <ul>{formatCommaSeparatedList(diag.procedureSet)}</ul>
            </div>

            {canEdit && (
              <div className="paged-action-row">
                <button
                  type="button"
                  className="edit-button"
                  onClick={() => handleEdit(currentIndex)}
                >
                  Edit
                </button>

                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => handleDelete(auth.authId)}
                >
                  Delete
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {authorizations.length > 1 && (
        <div className="paged-controls">
          <button
            type="button"
            className="edit-button"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
          >
            Previous
          </button>

          <span>
            {currentIndex + 1} / {authorizations.length}
          </span>

          <button
            type="button"
            className="edit-button"
            onClick={handleNext}
            disabled={currentIndex === authorizations.length - 1}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AuthorizationSection;